import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  InputText,
  SectionCard,
  Select,
  Tabs,
  TextArea,
  type SelectOption,
} from "@/deps/ui-library";
import {
  useFiscalYears,
  useMarkCheckDone,
  useMarkCheckNotApplicable,
  useReopenCheck,
  useReviewCheckItem,
  useYearChecklist,
} from "@/api/queries";
import type { YearCloseItem, YearCloseWorkstream } from "@/api/types";
import { useCan } from "@/app/session";
import { PageHeader } from "@/components/PageHeader";
import { YearBalanceTransferCard } from "@/features/settings/tabs/YearBalanceTransferCard";
import { YearApprovalCard } from "./YearApprovalCard";
import { YearCloseLog } from "./YearCloseLog";
import { YearAdjustmentsTab, YearPreviewTab } from "./YearWorkpaperTabs";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";

const STATUS: Record<
  YearCloseWorkstream["status"],
  { label: string; scheme: "default" | "success" | "info" }
> = {
  open: { label: "Open", scheme: "default" },
  done: { label: "Done", scheme: "success" },
  na: { label: "Not applicable", scheme: "info" },
};

function ItemLink({ item }: { item: YearCloseItem }) {
  const label = `Open ${item.kind}`;
  const params = { id: item.documentId };
  const className = "text-primary text-xs underline";
  switch (item.kind) {
    case "invoice":
      return (
        <Link to="/sales/invoices/$id" params={params} className={className}>
          {label}
        </Link>
      );
    case "credit note":
      return (
        <Link to="/sales/credit-notes/$id" params={params} className={className}>
          {label}
        </Link>
      );
    case "bill":
      return (
        <Link to="/purchases/bills/$id" params={params} className={className}>
          {label}
        </Link>
      );
    case "journal":
      return (
        <Link to="/accounting/journals/$id" params={params} className={className}>
          {label}
        </Link>
      );
    default:
      return (
        <Link to="/purchases/payments/$id" params={params} className={className}>
          {label}
        </Link>
      );
  }
}

/** One workstream: what to review, the note and evidence, and the outcome (ADR-0057). */
function WorkstreamDetail({
  yearId,
  workstream,
  locked,
}: {
  yearId: string;
  workstream: YearCloseWorkstream;
  locked: boolean;
}) {
  const can = useCan();
  const review = useReviewCheckItem(yearId);
  const done = useMarkCheckDone(yearId);
  const notApplicable = useMarkCheckNotApplicable(yearId);
  const reopen = useReopenCheck(yearId);
  const [note, setNote] = useState(workstream.note ?? "");
  const [evidence, setEvidence] = useState(workstream.evidence ?? "");

  const mayAct = can("accounting.periods.manage") && !locked;
  const open = workstream.status === "open";
  const toReview = workstream.items.filter((item) => !item.reviewed).length;
  const needs = [
    ...(toReview > 0 ? [`${toReview} item${toReview === 1 ? "" : "s"} to review`] : []),
    ...(evidence.trim() === "" ? ["a filing reference"] : []),
  ];
  const failure = review.error ?? done.error ?? notApplicable.error ?? reopen.error;

  return (
    <SectionCard title={workstream.name}>
      <div className="flex flex-col gap-4 text-sm">
        {failure ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(failure as Error).message}
          />
        ) : null}

        {workstream.items.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {workstream.items.map((item) => (
              <li
                key={item.key}
                className="border-border flex flex-wrap items-center justify-between gap-3 border-b pb-2"
              >
                <div className="min-w-0">
                  <p className="text-fg">
                    {item.number ?? `Draft ${item.kind}`} · {item.party}
                  </p>
                  <p className="text-fg-muted text-xs">
                    {item.date ? `${formatDate(item.date)} · ` : ""}
                    <span className="num">{formatAmount(item.amount)}</span> · {item.why}
                  </p>
                </div>
                <span className="flex items-center gap-3">
                  <ItemLink item={item} />
                  <Checkbox
                    label="Reviewed"
                    checked={item.reviewed}
                    disabled={!mayAct || !open || review.isPending}
                    onCheckedChange={(details: { checked: boolean | "indeterminate" }) =>
                      review.mutate({
                        workstream: workstream.id,
                        key: item.key,
                        reviewed: details.checked === true,
                      })
                    }
                  />
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-fg-muted">
            The books have nothing to list for this one. Check it, and record the evidence.
          </p>
        )}
        {workstream.moreItems > 0 ? (
          <p className="text-fg-muted text-xs">
            And {workstream.moreItems} more. Resolve these, and the rest will show.
          </p>
        ) : null}

        {open ? (
          <>
            <InputText
              label="Filing reference"
              placeholder={workstream.evidenceHint}
              helperText="Where the evidence is kept, such as a file or folder name."
              value={evidence}
              disabled={!mayAct}
              onChange={(event) => setEvidence(event.target.value)}
            />
            <TextArea
              label="Note"
              rows={3}
              value={note}
              disabled={!mayAct}
              onChange={(event) => setNote(event.target.value)}
            />
            {mayAct ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  colorScheme="primary"
                  variant="solid"
                  size="sm"
                  disabled={needs.length > 0 || done.isPending}
                  onClick={() =>
                    done.mutate({ workstream: workstream.id, evidence, note: note || undefined })
                  }
                >
                  Mark done
                </Button>
                <Button
                  colorScheme="default"
                  variant="outline"
                  size="sm"
                  disabled={note.trim() === "" || notApplicable.isPending}
                  onClick={() => notApplicable.mutate({ workstream: workstream.id, note })}
                >
                  Not applicable
                </Button>
                {needs.length > 0 ? (
                  <span className="text-fg-muted text-xs">
                    Mark done needs {needs.join(" and ")}.
                  </span>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <>
            <p className="text-fg">
              {workstream.status === "done"
                ? `Done by ${workstream.actedBy || "someone"}. Evidence: ${workstream.evidence}.`
                : `Not applicable, marked by ${workstream.actedBy || "someone"}.`}
              {workstream.actedAt ? ` ${formatDate(workstream.actedAt.slice(0, 10))}.` : ""}
            </p>
            {workstream.note ? <p className="text-fg-muted">{workstream.note}</p> : null}
            {mayAct ? (
              <div>
                <Button
                  colorScheme="default"
                  variant="outline"
                  size="sm"
                  disabled={reopen.isPending}
                  onClick={() => reopen.mutate(workstream.id)}
                >
                  Reopen
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </SectionCard>
  );
}

/** The year-end workspace: check accounts, close the year, and what's been logged (ADR-0056, ADR-0057). */
export function YearEndPage() {
  const { data: years } = useFiscalYears();
  const [chosenYear, setChosenYear] = useState<string | null>(null);
  const nextToClose =
    [...(years ?? [])]
      .sort((a, b) => a.start.localeCompare(b.start))
      .find((y) => y.status !== "closed") ?? years?.[0];
  // Held once it's known. Otherwise closing a year would move the page on to the next one, under the
  // person still reading what they've just closed.
  if (chosenYear === null && nextToClose) setChosenYear(nextToClose.id);
  const year = years?.find((y) => y.id === chosenYear) ?? nextToClose;
  const { data, isPending, error, refetch } = useYearChecklist(year?.id);
  const [chosenStream, setChosenStream] = useState("tx");
  const selected = data?.workstreams.find((w) => w.id === chosenStream);
  const resolved = data?.workstreams.filter((w) => w.status !== "open").length ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Year-end close"
        subtitle={
          data
            ? `${data.fiscalYearCode} · ${resolved} of ${data.workstreams.length} workstreams resolved${data.locked ? " · closed" : ""}`
            : undefined
        }
      />

      <div className="max-w-xs">
        <Select
          label="Fiscal year"
          options={(years ?? []).map((y) => ({ value: y.id, label: y.label }))}
          value={year?.id ?? ""}
          onChange={(details: { value: SelectOption | SelectOption[] | null }) => {
            const option = Array.isArray(details.value) ? details.value[0] : details.value;
            if (option) setChosenYear(String(option.value));
          }}
          showClearButton={false}
        />
      </div>

      {error ? (
        <div className="flex flex-col items-start gap-2">
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(error as Error).message}
          />
          <Button colorScheme="default" variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : null}
      {isPending && year ? <p className="text-fg-muted text-sm">Loading the checklist…</p> : null}

      {data && year ? (
        <Tabs.Root defaultValue="checks">
          <Tabs.List aria-label="Year-end close">
            <Tabs.Trigger value="checks">Check accounts</Tabs.Trigger>
            <Tabs.Trigger value="adjustments">Review adjustments</Tabs.Trigger>
            <Tabs.Trigger value="preview">Preview close</Tabs.Trigger>
            <Tabs.Trigger value="close">Approve and lock</Tabs.Trigger>
            <Tabs.Trigger value="log">Close log</Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="checks">
            <div className="grid gap-4 pt-4 lg:grid-cols-3">
              <SectionCard title="Workstreams" flush>
                <ul className="flex flex-col">
                  {data.workstreams.map((workstream) => (
                    <li key={workstream.id} className="border-border border-b last:border-b-0">
                      <Button
                        colorScheme="default"
                        variant="ghost"
                        aria-current={workstream.id === chosenStream ? "true" : undefined}
                        className={`h-auto w-full justify-between gap-2 rounded-none px-4 py-2.5 text-left font-normal ${
                          workstream.id === chosenStream ? "bg-muted" : ""
                        }`}
                        onClick={() => setChosenStream(workstream.id)}
                      >
                        <span className="text-fg">
                          {workstream.name}
                          {workstream.items.length > 0 ? (
                            <span className="text-fg-muted ml-1 text-xs">
                              · {workstream.items.filter((i) => !i.reviewed).length} to review
                            </span>
                          ) : null}
                        </span>
                        <Badge
                          variant="soft"
                          size="sm"
                          colorScheme={STATUS[workstream.status].scheme}
                        >
                          {STATUS[workstream.status].label}
                        </Badge>
                      </Button>
                    </li>
                  ))}
                </ul>
              </SectionCard>
              <div className="lg:col-span-2">
                {selected ? (
                  <WorkstreamDetail
                    key={`${year.id}:${selected.id}:${selected.status}`}
                    yearId={year.id}
                    workstream={selected}
                    locked={data.locked}
                  />
                ) : null}
              </div>
            </div>
          </Tabs.Content>

          <Tabs.Content value="adjustments">
            <YearAdjustmentsTab fiscalYearId={year.id} />
          </Tabs.Content>

          <Tabs.Content value="preview">
            <YearPreviewTab fiscalYearId={year.id} />
          </Tabs.Content>

          <Tabs.Content value="close">
            <div className="flex max-w-2xl flex-col gap-4 pt-4">
              <YearApprovalCard fiscalYearId={year.id} />
              <YearBalanceTransferCard fiscalYearId={year.id} />
            </div>
          </Tabs.Content>

          <Tabs.Content value="log">
            <div className="pt-4">
              <YearCloseLog code={data.fiscalYearCode} log={data.log} />
            </div>
          </Tabs.Content>
        </Tabs.Root>
      ) : null}
    </div>
  );
}
